<template>
  <div class="app-layout" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <!-- Sidebar -->
    <aside class="sidebar" :class="{ 'collapsed': sidebarCollapsed }">
      <div class="sidebar-header">
        <div class="logo">
          <h1 style="color: white">HCET</h1>
          <span>Hercor College Express Transaction</span>
        </div>
      </div>
      <nav class="sidebar-nav">
        <ul>
          <!-- Admin links -->
          <template v-if="userRole === 'admin'">
            <li>
              <router-link to="/admin/dashboard">
                <i class="fas fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/students">
                <i class="fas fa-user-graduate"></i>
                <span>Students</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/transaction-requirements">
                <i class="fas fa-clipboard-list"></i>
                <span>Transaction Requirements</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/transaction-management">
                <i class="fas fa-trash"></i>
                <span>Transaction Management</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/priority-codes">
                <i class="fas fa-key"></i>
                <span>Priority Codes</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/users">
                <i class="fas fa-users"></i>
                <span>Users</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/reports">
                <i class="fas fa-chart-bar"></i>
                <span>Reports</span>
              </router-link>
            </li>
            <li>
              <router-link to="/admin/settings">
                <i class="fas fa-cog"></i>
                <span>Settings</span>
              </router-link>
            </li>
          </template>
          
          <!-- Teller links -->
          <template v-else-if="userRole === 'teller'">
            <li>
              <router-link to="/teller/dashboard">
                <i class="fas fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </router-link>
            </li>
            <li>
              <router-link to="/teller/queue">
                <i class="fas fa-user-clock"></i>
                <span>Queue</span>
              </router-link>
            </li>
            <li>
              <router-link to="/teller/reports">
                <i class="fas fa-chart-bar"></i>
                <span>Reports</span>
              </router-link>
            </li>
          </template>
          
          <!-- Registrar links -->
          <template v-else-if="userRole === 'registrar'">
            <li>
              <router-link to="/registrar/dashboard">
                <i class="fas fa-tachometer-alt"></i>
                <span>Dashboard</span>
              </router-link>
            </li>
            <li>
              <router-link to="/registrar/reports">
                <i class="fas fa-chart-bar"></i>
                <span>Reports</span>
              </router-link>
            </li>
          </template>
        </ul>
      </nav>
    </aside>
    
    <!-- Sidebar toggle button -->
    <button class="sidebar-toggle" @click="toggleSidebar" style="margin-left: -20px; width: 36px; height: 36px; border: 1px solid #FFFFFF;">
      <i :class="['fas', sidebarCollapsed ? 'fa-chevron-right' : 'fa-chevron-left']"></i>
    </button>
    
    <!-- Main content -->
    <div class="main-content">
      <header class="top-header">
        <div class="header-content">
          <div class="page-title">{{ pageTitle }}</div>
          <div class="user-menu">
            <span class="user-name">{{ userName }}</span>
            <button class="btn btn-sm logout-btn" @click="showLogoutDialog = true" style="background-color: #2c3e50; color: white;">
              <i class="fas fa-sign-out-alt me-1"></i> Logout
            </button>
          </div>
        </div>
      </header>
      <div id="content-wrapper">
        <slot></slot>
      </div>
    </div>
    
    <!-- Logout confirmation modal -->
    <div class="logout-modal" v-if="showLogoutDialog">
      <div class="logout-modal-content">
        <div class="logout-modal-header">
          <h4>Confirm Logout</h4>
          <button class="close-btn" @click="showLogoutDialog = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="logout-modal-body">
          <p>Are you sure you want to log out of your account?</p>
        </div>
        <div class="logout-modal-footer">
          <button class="btn btn-secondary" @click="showLogoutDialog = false">
            Cancel
          </button>
          <button class="btn btn-primary" @click="handleLogout">
            <i class="fas fa-sign-out-alt me-1"></i> Logout
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'TheLayout',
  data() {
    return {
      showLogoutDialog: false
    }
  },
  computed: {
    ...mapGetters([
      'sidebarCollapsed'
    ]),
    ...mapGetters('auth', [
      'currentUser',
      'userRole'
    ]),
    userName() {
      return this.currentUser?.name || 'User'
    },
    pageTitle() {
      // Extract the page name from the current route
      const path = this.$route.path
      
      if (path.includes('dashboard')) {
        return 'Dashboard'
      } else if (path.includes('users')) {
        return 'User Management'
      } else if (path.includes('reports')) {
        return 'Reports'
      } else if (path.includes('settings')) {
        return 'Settings'
      } else if (path.includes('queue')) {
        return 'Queue Management'
      } else if (path.includes('students')) {
        return 'Student Records'
      } else if (path.includes('transaction-requirements')) {
        return 'Transaction Requirements'
      } else if (path.includes('transaction-management')) {
        return 'Transaction Management'
      } else if (path.includes('priority-codes')) {
        return 'Priority Codes'
      }

      
      return 'Dashboard'
    }
  },
  methods: {
    ...mapActions([
      'toggleSidebar'
    ]),
    ...mapActions('auth', [
      'logout'
    ]),
    logoutConfirm() {
      this.showLogoutDialog = true
    },
    async handleLogout() {
      try {
        await this.logout()
        this.showLogoutDialog = false
        this.$router.push('/login')
      } catch (error) {
        console.error('Error during logout:', error)
      }
    }
  }
}
</script>

<style scoped>
/* Additional component-specific styles */

/* App layout container */
.app-layout {
  display: flex;
  min-height: 100vh;
}

/* Sidebar styles */
.sidebar {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 260px;
  background-color: #2c3e50;
  color: white;
  overflow-y: auto;
  z-index: 1000;
  transition: width 0.3s ease;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.logo h1 {
  margin: 0;
  font-size: 1.75rem;
}

.logo span {
  font-size: 0.8rem;
  opacity: 0.8;
}

.sidebar-nav {
  padding: 15px 0;
}

.sidebar-nav ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-nav li {
  margin: 5px 0;
}

.sidebar-nav a {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  border-left: 3px solid transparent;
}

.sidebar-nav a:hover, .sidebar-nav a.router-link-active {
  color: white;
  background-color: rgba(255, 255, 255, 0.1);
  border-left-color: #007bff;
}

.sidebar-nav i {
  margin-right: 10px;
  width: 20px;
  text-align: center;
}

.sidebar.collapsed .logo span,
.sidebar.collapsed .sidebar-nav span {
  display: none;
}

/* Main content styles */
.main-content {
  flex: 1;
  margin-left: 260px; /* Same as sidebar width */
  transition: margin-left 0.3s ease;
}

.app-layout.sidebar-collapsed .main-content {
  margin-left: 70px; /* Same as collapsed sidebar width */
}

/* Sidebar toggle button */
.sidebar-toggle {
  position: fixed;
  left: 260px; /* Position right at the edge of the expanded sidebar */
  top: 50%;
  transform: translateY(-50%);
  background-color: #2c3e50;
  color: white;
  border: none;
  border-radius: 0 4px 4px 0;
  padding: 8px;
  cursor: pointer;
  z-index: 1000;
  transition: left 0.3s ease;
}

.app-layout.sidebar-collapsed .sidebar-toggle {
  left: 70px; /* Move with the sidebar when collapsed */
}

/* Header styles */
.top-header {
  height: 60px;
  background-color: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 0 20px;
  position: sticky;
  top: 0;
  z-index: 990;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
}

.page-title {
  font-size: 1.25rem;
  font-weight: 500;
}

.user-menu {
  display: flex;
  align-items: center;
  gap: 10px;
}

.user-name {
  font-weight: 500;
}

.logout-btn {
  padding: 0.25rem 0.5rem;
}

/* Content wrapper */
#content-wrapper {
  padding: 20px;
}

/* Modal styles */
.logout-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.logout-modal-content {
  background-color: white;
  border-radius: 5px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.logout-modal-header {
  padding: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logout-modal-header h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 500;
}

.close-btn {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: #6c757d;
}

.logout-modal-body {
  padding: 20px 15px;
}

.logout-modal-footer {
  padding: 15px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn {
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  font-weight: 500;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.me-1 {
  margin-right: 0.25rem;
}
</style> 